/**
 * AdSystem - Placeholder for mobile ad integration
 *
 * Per PRD section 3.4:
 * - FR-022: Show interstitial every 5 completed levels
 * - FR-023: If ad fails to load, skip silently
 * - FR-024: Game fully playable offline - ads just don't show
 *
 * This is a placeholder that prepares the interface for AdMob integration
 * when Capacitor is set up. Ad unit IDs are read from environment variables.
 *
 * SECURITY: Ad unit IDs are NEVER stored in code - use .env files
 */

// =============================================================================
// TYPES
// =============================================================================
import { devLog } from '../utils/devLog';

export interface AdConfig {
  /** AdMob interstitial ad unit ID (from environment variable) */
  interstitialAdUnitId?: string;
  /** Whether ads are enabled (can be disabled in settings or offline) */
  enabled: boolean;
}

export interface AdSystemState {
  /** Whether an interstitial ad is currently loaded and ready */
  interstitialReady: boolean;
  /** Whether AdMob has been initialized */
  initialized: boolean;
  /** Last error message (for debugging) */
  lastError: string | null;
}

// =============================================================================
// AD SYSTEM
// =============================================================================

export class AdSystem {
  private config: AdConfig = {
    interstitialAdUnitId: undefined,
    enabled: true,
  };

  private state: AdSystemState = {
    interstitialReady: false,
    initialized: false,
    lastError: null,
  };

  // Track levels completed for interstitial timing
  private levelsCompletedSinceLastAd: number = 0;
  private static readonly LEVELS_BETWEEN_ADS = 5;

  constructor() {
    this.loadConfig();
  }

  // ===========================================================================
  // INITIALIZATION
  // ===========================================================================

  /**
   * Loads ad configuration from environment variables
   * Ad unit IDs should be in .env file, never hardcoded
   */
  private loadConfig(): void {
    // Read ad unit ID from environment variable
    // In Vite, env vars prefixed with VITE_ are exposed to client
    const interstitialId = import.meta.env.VITE_ADMOB_INTERSTITIAL_ID as string | undefined;

    this.config.interstitialAdUnitId = interstitialId;

    // Log config status (without revealing actual IDs)
    if (interstitialId) {
      devLog('[AdSystem] Interstitial ad unit ID configured');
    } else {
      devLog('[AdSystem] No interstitial ad unit ID configured - ads disabled');
    }
  }

  /**
   * Initializes the ad system
   * In a real implementation, this would initialize the AdMob SDK via Capacitor
   *
   * @returns Promise that resolves when initialization is complete
   */
  async initialize(): Promise<void> {
    if (this.state.initialized) return;

    // Check if we have an ad unit ID configured
    if (!this.config.interstitialAdUnitId) {
      devLog('[AdSystem] Skipping initialization - no ad unit ID configured');
      this.config.enabled = false;
      return;
    }

    try {
      // PLACEHOLDER: In production, this would call AdMob.initialize()
      // via @capacitor-community/admob or similar plugin
      //
      // Example:
      // import { AdMob } from '@capacitor-community/admob';
      // await AdMob.initialize();

      this.state.initialized = true;
      devLog('[AdSystem] Initialized (placeholder)');

      // Pre-load first interstitial
      await this.loadInterstitial();
    } catch (error) {
      // FR-023: If ad fails to load, skip silently
      this.state.lastError = error instanceof Error ? error.message : 'Unknown error';
      devLog('[AdSystem] Initialization failed silently:', this.state.lastError);
    }
  }

  // ===========================================================================
  // INTERSTITIAL ADS
  // ===========================================================================

  /**
   * Pre-loads an interstitial ad
   * Should be called after showing an ad to prepare the next one
   */
  async loadInterstitial(): Promise<void> {
    if (!this.config.enabled || !this.config.interstitialAdUnitId) {
      return;
    }

    try {
      // PLACEHOLDER: In production, this would call AdMob.prepareInterstitial()
      //
      // Example:
      // import { AdMob, AdOptions } from '@capacitor-community/admob';
      // const options: AdOptions = {
      //   adId: this.config.interstitialAdUnitId,
      // };
      // await AdMob.prepareInterstitial(options);

      this.state.interstitialReady = true;
      devLog('[AdSystem] Interstitial ad loaded (placeholder)');
    } catch (error) {
      // FR-023: If ad fails to load, skip silently
      this.state.interstitialReady = false;
      this.state.lastError = error instanceof Error ? error.message : 'Unknown error';
      devLog('[AdSystem] Failed to load interstitial silently');
    }
  }

  /**
   * Shows an interstitial ad if one is ready
   *
   * @returns Promise that resolves when ad is dismissed (or immediately if no ad)
   */
  async showInterstitial(): Promise<void> {
    if (!this.config.enabled) {
      devLog('[AdSystem] Ads disabled, skipping interstitial');
      return;
    }

    if (!this.state.interstitialReady) {
      devLog('[AdSystem] No interstitial ready, skipping');
      return;
    }

    try {
      // PLACEHOLDER: In production, this would call AdMob.showInterstitial()
      //
      // Example:
      // import { AdMob } from '@capacitor-community/admob';
      // await AdMob.showInterstitial();

      devLog('[AdSystem] Would show interstitial ad here');
      this.state.interstitialReady = false;

      // Pre-load next interstitial
      await this.loadInterstitial();
    } catch (error) {
      // FR-023: If ad fails to show, skip silently
      this.state.lastError = error instanceof Error ? error.message : 'Unknown error';
      devLog('[AdSystem] Failed to show interstitial silently');
    }
  }

  /**
   * Called when a level is completed
   * Tracks completion count and shows interstitial every 5 levels (FR-022)
   *
   * @returns Promise that resolves when ad is dismissed (or immediately if no ad shown)
   */
  async onLevelComplete(): Promise<void> {
    this.levelsCompletedSinceLastAd++;

    // FR-022: Show interstitial every 5 completed levels
    if (this.levelsCompletedSinceLastAd >= AdSystem.LEVELS_BETWEEN_ADS) {
      devLog(`[AdSystem] ${this.levelsCompletedSinceLastAd} levels completed, showing interstitial`);
      this.levelsCompletedSinceLastAd = 0;
      await this.showInterstitial();
    }
  }

  // ===========================================================================
  // SETTINGS
  // ===========================================================================

  /**
   * Enables or disables ads
   * Used when user is offline or has a premium version (future feature)
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
    devLog(`[AdSystem] Ads ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Returns whether ads are currently enabled
   */
  isEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * Returns whether an interstitial is ready to show
   */
  isInterstitialReady(): boolean {
    return this.state.interstitialReady;
  }

  // ===========================================================================
  // UTILITIES
  // ===========================================================================

  /**
   * Checks if the device is online
   * FR-024: Game fully playable offline - ads just don't show
   */
  isOnline(): boolean {
    if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
      return navigator.onLine;
    }
    return true; // Assume online if we can't detect
  }

  /**
   * Resets the level completion counter
   * Call this when resetting game progress
   */
  resetLevelCounter(): void {
    this.levelsCompletedSinceLastAd = 0;
  }

  /**
   * Gets the current state (for debugging)
   */
  getState(): AdSystemState {
    return { ...this.state };
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

export const adSystem = new AdSystem();
